import React, { useMemo, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { GENEROS_TREE } from '../../constants/musicConstants.js';
import {
  COMUNIDAD_GENERO_DESCONOCIDO,
  COMUNIDAD_GENERO_MULTICULTURAL,
  COMUNIDAD_MAX_MUSIC_GENRES,
} from '../../lib/comunidadMusicGenres.js';

const CATEGORIES = Object.keys(GENEROS_TREE);

const modeBtn = (active, dark = true) => ({
  padding: '8px 12px',
  borderRadius: 10,
  fontSize: 11,
  fontWeight: active ? 700 : 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
  border: active
    ? `0.5px solid ${C.gold}`
    : dark
      ? '0.5px solid rgba(255,255,255,0.18)'
      : '0.5px solid rgba(0,0,0,0.12)',
  background: active ? (dark ? 'rgba(200,168,75,0.14)' : 'rgba(200,168,75,0.12)') : dark ? 'rgba(255,255,255,0.04)' : '#fff',
  color: active ? C.gold : dark ? 'rgba(255,255,255,0.72)' : '#444',
});

const chipBtn = (active, dark = true) => ({
  padding: '5px 10px',
  borderRadius: 16,
  fontSize: 10,
  fontWeight: active ? 700 : 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
  border: active
    ? `0.5px solid ${C.gold}`
    : dark
      ? '0.5px solid rgba(255,255,255,0.14)'
      : '0.5px solid rgba(0,0,0,0.1)',
  background: active ? (dark ? 'rgba(200,168,75,0.18)' : 'rgba(200,168,75,0.15)') : 'transparent',
  color: active ? C.gold : dark ? 'rgba(255,255,255,0.65)' : '#555',
});

/**
 * Selector de género(s) musical(es) para publicaciones Comunidad.
 * @param {{ mode: string, genres: string[], onChange: (patch: { musicGenreMode?: string, musicGenres?: string[] }) => void, variant?: 'dark'|'light' }} props
 */
export default function ComunidadMusicGenresField({ mode, genres, onChange, variant = 'dark' }) {
  const dark = variant !== 'light';
  const [category, setCategory] = useState(CATEGORIES[0] || 'Rock / Metal');
  const selected = Array.isArray(genres) ? genres : [];
  const atMax = selected.length >= COMUNIDAD_MAX_MUSIC_GENRES;

  const subs = useMemo(() => GENEROS_TREE[category] || [], [category]);

  const setMode = next => {
    if (next === 'multicultural' || next === 'desconocido') {
      onChange({ musicGenreMode: next, musicGenres: [] });
      return;
    }
    onChange({ musicGenreMode: 'specific', musicGenres: selected });
  };

  const toggleGenre = g => {
    if (mode !== 'specific') {
      onChange({ musicGenreMode: 'specific', musicGenres: [g] });
      return;
    }
    if (selected.includes(g)) {
      onChange({ musicGenreMode: 'specific', musicGenres: selected.filter(x => x !== g) });
      return;
    }
    if (atMax) return;
    onChange({ musicGenreMode: 'specific', musicGenres: [...selected, g] });
  };

  const lbl = {
    fontSize: 11,
    color: dark ? 'rgba(255,255,255,0.5)' : '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    display: 'block',
    marginBottom: 6,
  };

  return (
    <div>
      <label style={lbl}>Género musical *</label>
      <p style={{ fontSize: 11, color: dark ? 'rgba(255,255,255,0.42)' : '#888', margin: '0 0 10px', lineHeight: 1.45 }}>
        Ayuda a buscar eventos y lugares. Puedes marcar varios estilos (máx. {COMUNIDAD_MAX_MUSIC_GENRES}), o indicar multicultural / desconocido.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <button type="button" style={modeBtn(mode === 'specific', dark)} onClick={() => setMode('specific')}>
          Elegir géneros
        </button>
        <button type="button" style={modeBtn(mode === 'multicultural', dark)} onClick={() => setMode('multicultural')}>
          {COMUNIDAD_GENERO_MULTICULTURAL}
        </button>
        <button type="button" style={modeBtn(mode === 'desconocido', dark)} onClick={() => setMode('desconocido')}>
          {COMUNIDAD_GENERO_DESCONOCIDO}
        </button>
      </div>

      {mode === 'specific' ? (
        <>
          {selected.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {selected.map(g => (
                <button
                  key={g}
                  type="button"
                  onClick={() => toggleGenre(g)}
                  style={{ ...chipBtn(true, dark), display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  title="Quitar"
                >
                  {g} <span aria-hidden>×</span>
                </button>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: 10, color: dark ? 'rgba(255,200,120,0.85)' : '#b45309', margin: '0 0 8px' }}>
              Selecciona al menos un género de la lista.
            </p>
          )}

          <div style={{ marginBottom: 8 }}>
            <label style={{ ...lbl, fontSize: 10, marginBottom: 4 }}>Categoría</label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: 8,
                border: dark ? '0.5px solid rgba(255,255,255,0.15)' : '0.5px solid rgba(0,0,0,0.12)',
                background: dark ? 'rgba(255,255,255,0.06)' : '#fff',
                color: dark ? '#fff' : '#111',
                fontSize: 12,
                fontFamily: 'inherit',
              }}
            >
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              maxHeight: 140,
              overflowY: 'auto',
              padding: '4px 2px',
            }}
          >
            {subs.map(g => (
              <button
                key={g}
                type="button"
                disabled={!selected.includes(g) && atMax}
                onClick={() => toggleGenre(g)}
                style={{
                  ...chipBtn(selected.includes(g), dark),
                  opacity: !selected.includes(g) && atMax ? 0.45 : 1,
                }}
              >
                {g}
              </button>
            ))}
          </div>
          {atMax ? (
            <p style={{ fontSize: 10, color: dark ? 'rgba(255,255,255,0.38)' : '#999', margin: '8px 0 0' }}>
              Máximo {COMUNIDAD_MAX_MUSIC_GENRES} géneros. Quita uno para añadir otro.
            </p>
          ) : null}
        </>
      ) : (
        <p style={{ fontSize: 11, color: dark ? 'rgba(255,255,255,0.55)' : '#666', margin: 0, fontStyle: 'italic' }}>
          {mode === 'multicultural'
            ? 'Se mostrará como espacio con programación variada o sin estilo único.'
            : 'Se mostrará sin género concreto; puedes detallarlo en «Detalles» si quieres.'}
        </p>
      )}
    </div>
  );
}
